import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { setSmsContent } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, SmsSendInputState } from 'domains/campaigns/types';
import { SMS } from 'domains/content/types';
import AutocompleteContent from 'domains/core/components/AutocompleteContent';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import useSmses from 'hooks/queries/useSmses';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
};

const ContentWrapper = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
}: Props) => {
    const currentSend = campaignState.sends[activeSendIndex];
    const { content } = currentSend as SmsSendInputState;

    const [contentInputValue, setContentInputValue] = useState('');
    const [selectedContentOption, setSelectedContentOption] = useState<AutocompleteOption<SMS>>();

    const { data: smses } = useSmses();

    useEffect(() => {
        if (content.id) {
            const selectedSms = smses?.find((sms) => sms.id === content.id);
            const smsOption = {
                index: 1,
                label: selectedSms?.name,
                value: selectedSms,
            };

            setContentInputValue(content.name);
            setSelectedContentOption(smsOption);
        }
    }, [content.id, content.name, smses]);

    const clearContentFields = () => {
        dispatchCampaignAction(setSmsContent({ contentId: null, contentName: '' }, activeSendIndex));
    };

    const updateContentFields = ({ value }: AutocompleteOption<SMS>) => {
        dispatchCampaignAction(setSmsContent({ contentId: value.id, contentName: value.name }, activeSendIndex));
    };

    const handleContentSelection = (_e: any, option: AutocompleteOption<any>) => {
        if (option) {
            updateContentFields(option);
            setContentPreviewId(!!contentPreviewId ? option.value.id : null);
        } else {
            clearContentFields();
            setContentPreviewId(null);
            setSelectedContentOption(null);
        }
    };

    const handlePreviewClick = () => {
        setContentPreviewId(!!contentPreviewId ? null : content.id);
    };

    return (
        <Stack rowGap={5}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" width="100%">
                <AutocompleteContent
                    contentType={CampaignChannels.SMS}
                    isPreviewVisible={!!contentPreviewId}
                    onChange={handleContentSelection}
                    onContentPreviewClick={handlePreviewClick}
                    onInputChange={(_e, inputValue) => setContentInputValue(inputValue)}
                    textfieldInput={contentInputValue}
                    value={selectedContentOption ?? null}
                />
            </Stack>
        </Stack>
    );
};

export default ContentWrapper;
